import React, { useState } from 'react'
import { Card, Input, Space, Button, Table, DatePicker, Tag, Modal, Typography } from 'antd'
import { SearchOutlined, FileProtectOutlined, EyeOutlined } from '@ant-design/icons'
import { useAuditLogs } from '../hooks/useAuditLogs'
import { useAdmins } from '../../admins/hooks/useAdmins'
import { UAParser } from 'ua-parser-js'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Text } = Typography

const formatAction = (action: string) => {
    if (!action) return 'Unknown';
    let formatted = action.replace(/^(audit|auth|user|admin)\./i, '');
    formatted = formatted.replace(/[._]/g, ' ');
    return formatted.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const getActionColor = (action: string) => {
    const lower = action.toLowerCase();
    if (lower.includes('failed') || lower.includes('error') || lower.includes('deleted')) return 'error';
    if (lower.includes('succeeded') || lower.includes('success') || lower.includes('created')) return 'success';
    if (lower.includes('updated') || lower.includes('refreshed')) return 'processing';
    return 'default';
}

export const AuditLogsScreen: React.FC = () => {
    const {
        logs,
        totalLogs,
        page, setPage,
        pageSize, setPageSize,
        action, setAction,
        userId, setUserId,
        setDateRange,
        isLoading,
        isFetching
    } = useAuditLogs()

    const { data: adminsResponse } = useAdmins();
    const adminsList = adminsResponse?.data || [];
    const adminMap = new Map(adminsList.map((a: any) => [a.id?.toString(), a.name]));

    const [selectedLog, setSelectedLog] = useState<any>(null)
    const [isModalVisible, setIsModalVisible] = useState(false)

    const handleViewDetails = (log: any) => {
        setSelectedLog(log)
        setIsModalVisible(true)
    }

    const columns: ColumnsType<any> = [
        {
            title: 'Date & Time',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
            width: 180,
        },
        {
            title: 'User',
            key: 'user',
            render: (_, record) => {
                const metadataUsername = record.metadata?.username || record.metadata?.user_name || record.metadata?.name;
                const mappedName = adminMap.get(record.user_id?.toString());
                const finalName = mappedName || metadataUsername;
                
                if (finalName) {
                    return <Text>{finalName}</Text>;
                }
                return record.user_id;
            },
            width: 150,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (text) => <Tag color={getActionColor(text)}>{formatAction(text)}</Tag>,
            width: 200,
        },
        {
            title: 'IP Address',
            dataIndex: 'ip_address',
            key: 'ip_address',
            width: 150,
        },
        {
            title: 'Details',
            key: 'details',
            render: (_, record) => (
                <Button 
                    type="link" 
                    icon={<EyeOutlined />} 
                    onClick={() => handleViewDetails(record)}
                >
                    View
                </Button>
            ),
            width: 100,
        },
    ]

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title={<><FileProtectOutlined /> Audit Logs</>}>
                <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                    <Input
                        placeholder="Search User ID"
                        prefix={<SearchOutlined />}
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Input
                        placeholder="Search Action"
                        prefix={<SearchOutlined />}
                        value={action}
                        onChange={(e) => setAction(e.target.value)}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <RangePicker 
                        onChange={(dates, dateStrings) => {
                            if (dates) {
                                setDateRange(dateStrings as [string, string])
                            } else {
                                setDateRange(null)
                            }
                        }} 
                    />
                    {/* Note: In a real app we'd implement the export feature matching other screens */}
                </Space>

                <Table
                    columns={columns}
                    dataSource={logs}
                    rowKey="id"
                    loading={isLoading || isFetching}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: totalLogs,
                        showSizeChanger: true,
                        onChange: (p, ps) => {
                            setPage(p)
                            setPageSize(ps)
                        }
                    }}
                    scroll={{ x: 800 }}
                />
            </Card>

            <Modal
                title="Audit Log Details"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={700}
            >
                {selectedLog && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Card size="small" type="inner" title="General Info">
                            <Text strong>Event ID: </Text> <Text>{selectedLog.id}</Text><br/>
                            <Text strong>User: </Text> <Text>{
                                adminMap.get(selectedLog.user_id?.toString()) || 
                                selectedLog.metadata?.username || 
                                selectedLog.metadata?.user_name || 
                                selectedLog.metadata?.name || 
                                selectedLog.user_id
                            }</Text><br/>
                            <Text strong>Action: </Text> <Text>{formatAction(selectedLog.action)}</Text><br/>
                            <Text strong>Timestamp: </Text> <Text>{dayjs(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
                        </Card>
                        <Card size="small" type="inner" title="Request Details">
                            <Text strong>IP Address: </Text> <Text>{selectedLog.ip_address}</Text><br/>
                            {selectedLog.user_agent ? (() => {
                                const parser = new UAParser(selectedLog.user_agent);
                                const browser = parser.getBrowser();
                                const os = parser.getOS();
                                const device = parser.getDevice();
                                return (
                                    <>
                                        <Text strong>Browser: </Text> <Text>{browser.name ? `${browser.name} ${browser.version || ''}`.trim() : 'Unknown'}</Text><br/>
                                        <Text strong>OS: </Text> <Text>{os.name ? `${os.name} ${os.version || ''}`.trim() : 'Unknown'}</Text><br/>
                                        <Text strong>Device: </Text> <Text>{(device.vendor || device.model) ? `${device.vendor || ''} ${device.model || ''}`.trim() : 'Desktop / Unknown'}</Text><br/>
                                        <Text strong>Raw UA: </Text> <Text type="secondary" style={{ fontSize: '12px' }}>{selectedLog.user_agent}</Text><br/>
                                    </>
                                );
                            })() : (
                                <><Text strong>User Agent: </Text> <Text>N/A</Text><br/></>
                            )}
                            <Text strong>Device ID: </Text> <Text>{selectedLog.device_id || 'N/A'}</Text>
                        </Card>
                        {selectedLog.metadata && (
                            <Card size="small" type="inner" title="Metadata">
                                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflowX: 'auto' }}>
                                    {JSON.stringify(selectedLog.metadata, null, 2)}
                                </pre>
                            </Card>
                        )}
                    </Space>
                )}
            </Modal>
        </Space>
    )
}
